import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import './SocialAuthButtons.css';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;
const APPLE_CLIENT_ID = process.env.REACT_APP_APPLE_CLIENT_ID;
const GOOGLE_SRC = 'https://accounts.google.com/gsi/client';
const APPLE_SRC = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';

// Load a third-party script once and resolve when ready. Subsequent callers
// reuse the same in-flight/loaded promise so we never inject duplicates.
function loadScript(src) {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) {
      if (existing.dataset.loaded === 'true') return resolve();
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', reject);
      return;
    }
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.defer = true;
    script.onload = () => { script.dataset.loaded = 'true'; resolve(); };
    script.onerror = reject;
    document.head.appendChild(script);
  });
}

/**
 * Google + Apple sign-in buttons. Renders the official Google Identity Services
 * button when REACT_APP_GOOGLE_CLIENT_ID is set, and an Apple button when
 * REACT_APP_APPLE_CLIENT_ID is set. When a provider isn't configured, its button
 * is shown disabled with a "coming soon" title so the page never breaks in dev.
 *
 * On success it stores the session (via AuthContext) and navigates by role,
 * mirroring the password-login redirect — so it works on both Login and Register.
 */
const SocialAuthButtons = ({ redirectTo, onError }) => {
  const { t, i18n } = useTranslation();
  const { loginWithGoogle, loginWithApple } = useAuth();
  const navigate = useNavigate();
  const googleBtnRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const redirectByRole = useCallback(() => {
    if (redirectTo) {
      navigate(redirectTo, { replace: true });
      return;
    }
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    if (userData.role === 'admin') navigate('/admin/dashboard', { replace: true });
    else if (userData.role === 'manager') navigate('/manager/dashboard', { replace: true });
    else navigate('/admin', { replace: true });
  }, [navigate, redirectTo]);

  // --- Google ---
  useEffect(() => {
    if (!GOOGLE_CLIENT_ID || !googleBtnRef.current) return;
    let cancelled = false;

    const handleCredential = async (response) => {
      if (!response?.credential) return;
      setBusy(true);
      const result = await loginWithGoogle(response.credential);
      setBusy(false);
      if (result.success) redirectByRole();
      else if (onError) onError(result.error);
    };

    loadScript(GOOGLE_SRC)
      .then(() => {
        if (cancelled || !window.google?.accounts?.id || !googleBtnRef.current) return;
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: handleCredential
        });
        googleBtnRef.current.innerHTML = '';
        window.google.accounts.id.renderButton(googleBtnRef.current, {
          type: 'standard',
          theme: 'outline',
          size: 'large',
          text: 'signin_with',
          logo_alignment: 'left',
          width: googleBtnRef.current.offsetWidth || 320,
          locale: i18n.language
        });
      })
      .catch(() => { if (onError) onError(t('auth.login.socialLoadError', 'Could not load Google sign-in.')); });

    return () => { cancelled = true; };
  }, [loginWithGoogle, redirectByRole, onError, t, i18n.language]);

  // --- Apple ---
  const handleApple = async () => {
    if (!APPLE_CLIENT_ID || busy) return;
    setBusy(true);
    try {
      await loadScript(APPLE_SRC);
      if (!window.AppleID?.auth) throw new Error('Apple SDK unavailable');
      window.AppleID.auth.init({
        clientId: APPLE_CLIENT_ID,
        scope: 'name email',
        redirectURI: window.location.origin,
        usePopup: true
      });
      const data = await window.AppleID.auth.signIn();
      const idToken = data?.authorization?.id_token;
      if (!idToken) throw new Error('No Apple id_token');
      // Apple sends the name only on the first authorization.
      const name = data?.user?.name
        ? [data.user.name.firstName, data.user.name.lastName].filter(Boolean).join(' ')
        : undefined;
      const result = await loginWithApple({ id_token: idToken, name });
      if (result.success) redirectByRole();
      else if (onError) onError(result.error);
    } catch (err) {
      // Popup closed / cancelled is not an error worth surfacing loudly.
      if (err?.error !== 'popup_closed_by_user' && onError) {
        onError(t('auth.login.appleError', 'Could not sign in with Apple.'));
      }
    } finally {
      setBusy(false);
    }
  };

  const comingSoon = t('auth.login.socialComingSoon', 'Coming soon');

  return (
    <div className="social-auth" aria-busy={busy}>
      {/* Google: official GIS button when configured, else a disabled placeholder. */}
      {GOOGLE_CLIENT_ID ? (
        <div className="social-auth-google" ref={googleBtnRef} />
      ) : (
        <button type="button" className="social-btn" disabled title={comingSoon} aria-label={t('auth.login.signInWithGoogle', 'Sign in with Google')}>
          <GoogleIcon />
          <span>{t('auth.login.signInWithGoogle', 'Sign in with Google')}</span>
        </button>
      )}

      {/* Apple: custom black button (requires HTTPS + Apple Developer config). */}
      <button
        type="button"
        className="social-btn social-btn-apple"
        onClick={handleApple}
        disabled={!APPLE_CLIENT_ID || busy}
        title={APPLE_CLIENT_ID ? undefined : comingSoon}
        aria-label={t('auth.login.signInWithApple', 'Sign in with Apple')}
      >
        <AppleIcon />
        <span>{t('auth.login.signInWithApple', 'Sign in with Apple')}</span>
      </button>
    </div>
  );
};

const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/>
  </svg>
);

const AppleIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden="true">
    <path d="M16.37 1.43c0 1.14-.42 2.2-1.25 3.04-.99 1.02-2.18 1.61-3.46 1.5a3.4 3.4 0 0 1-.03-.43c0-1.1.48-2.27 1.27-3.07.79-.81 2.1-1.42 3.2-1.47.03.3.27.6.27.9zM20.5 17.1c-.55 1.27-.82 1.84-1.53 2.96-.99 1.57-2.39 3.52-4.12 3.53-1.54.02-1.94-1.01-4.03-1-2.09.01-2.53 1.02-4.07 1-1.73-.01-3.05-1.77-4.04-3.34C-.05 18.04-.32 13.9 1.4 11.5c1.13-1.65 2.91-2.62 4.59-2.62 1.71 0 2.78 1.02 4.2 1.02 1.37 0 2.2-1.02 4.2-1.02 1.5 0 3.09.82 4.22 2.23-3.71 2.03-3.11 7.32 1.69 8.99z"/>
  </svg>
);

export default SocialAuthButtons;
